import type { Observable } from 'rxjs';
import type { WebLegacyWidgetEntity } from '@web/types';

import type { OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';

import type { AccordionEntity } from '@sites/entity-core';
import type { AccordionForm, AccordionItem } from '@sites/entity-signal-form';

import { LayoutFacade } from '@web/store/layout/layout.facade';
import { WebLegacyWidgetSignalFormService } from '@web/core/services/web-legacy-widget-signal-form.service';

import { WebBaseViewSignalFormComponent } from '@web/widgets/common/abstract/web-base-view-signal-form.component';
import {
    accordionDividerColorCss,
    accordionIconColorCss,
    accordionTransformCss,
    accordionTransformMobileCss,
    accordionZIndexCss,
    accordionZIndexMobileCss,
} from './accordion-view.css-vars';

import { EFaqIconStyle, EFaqIconPosition } from '@common/enums/faq-icon.enum';
import { CommonElementsModule } from '@web/widgets/common/common-elements.module';

/**
 * Editor preview for the `accordion` widget — signal-based. The form is shared with the control
 * panel via `WebLegacyWidgetSignalFormService` (ref-counted by widget id).
 *
 * **`toggleItem()` is untouched from the legacy component, verbatim.** The expand/collapse
 * mechanism is pure imperative DOM (`document.querySelector` scoped by the literal class names
 * `.itemContent_${i}`/`.itemControl_${i}`, toggling `.active`, writing `element.style.height`
 * directly with a `setTimeout` for the post-transition `height: 'auto'` flip) and never reads or
 * writes the form — `lastOpenItem` is pure transient UI state, same as before migration. Only
 * `getWidgetId` changed source (now the form's `displayId`, not `WebBaseViewComponent`'s).
 */
@Component({
    selector: 'web-accordion-view',
    templateUrl: './accordion-view.component.html',
    styleUrls: ['./accordion-view.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonElementsModule],
})
export class AccordionViewComponent
    extends WebBaseViewSignalFormComponent<WebLegacyWidgetEntity<AccordionEntity['data']>>
    implements OnInit
{
    private readonly signalFormService: WebLegacyWidgetSignalFormService = inject(WebLegacyWidgetSignalFormService);
    private readonly layoutFacade: LayoutFacade = inject(LayoutFacade);

    public readonly isDeviceMobile$: Observable<boolean> = this.layoutFacade.isDeviceMobile$;
    public readonly isDeviceTablet$: Observable<boolean> = this.layoutFacade.isDeviceTablet$;

    public form!: AccordionForm;

    public readonly iconStyle = EFaqIconStyle;
    public readonly iconPosition = EFaqIconPosition;
    public lastOpenItem: number = 0;

    public readonly accordionList = computed<AccordionItem[]>(() => this.form.accordionItems());

    // --- CSS custom property values (bound on the root/icon in the template) ---
    public readonly rootTransform = computed<string>(() => accordionTransformCss(this.form.position().value()?.rotate));
    public readonly rootTransformMobile = computed<string>(() =>
        accordionTransformMobileCss(this.form.position().value()?.mobileRotate)
    );
    public readonly rootZIndex = computed<string | null>(() =>
        accordionZIndexCss(this.form.position().value()?.zIndex)
    );
    public readonly rootZIndexMobile = computed<string | null>(() => {
        const position = this.form.position().value();
        return accordionZIndexMobileCss(
            this.form.isSynchronizeStyle().value(),
            position?.zIndex,
            position?.mobileZIndex
        );
    });
    public readonly dividerColor = computed<string>(() =>
        accordionDividerColorCss(this.form.themeIndentColor().value(), this.form.indentColor().value())
    );
    public readonly iconColor = computed<string>(() =>
        accordionIconColorCss(this.form.themeIconColor().value(), this.form.iconColor().value())
    );

    /** Displayed element id: the user's `displayId` if set, else `widget-${id}` — same fallback the
     *  legacy `WebBaseViewComponent.getWidgetId` used, now sourced from the form's envelope. */
    public get getWidgetId(): string {
        return this.form.displayId();
    }

    public get containerId(): string {
        return `widget_${this.form.id().value()}`;
    }

    public get widgetIdValue(): string {
        return this.form.id().value();
    }

    public ngOnInit(): void {
        this.form = this.signalFormService.getOrCreateScoped<AccordionForm>(this.widget().id, this.destroyRef);
    }

    public saveAccordionData(content: string, fieldType: string, index: number): void {
        this.form.updateAccordionItem(index, fieldType === 'title' ? { title: content } : { content });
    }

    public toggleItem(index: number) {
        const accordionList = [...this.accordionList()];
        const visibleItemHeight =
            (
                document.querySelector(`[id='${this.getWidgetId}'] .itemContent_${index} .sp-editor`) as HTMLDivElement
            )?.offsetHeight.toString() + 'px';

        if (this.form.isAutoCollapse().value()) {
            const visibleItem = document.querySelector(
                `[id='${this.getWidgetId}'] .itemContent_${index}`
            ) as HTMLDivElement;
            const openControl = document.querySelector(
                `[id='${this.getWidgetId}'] .itemControl_${index}`
            ) as HTMLDivElement;

            if (visibleItem.classList.contains('active')) {
                for (let i = 0; i < accordionList.length; i++) {
                    const hiddenItem = document.querySelector(
                        `[id='${this.getWidgetId}'] .itemContent_${i}`
                    ) as HTMLDivElement;
                    const closeControl = document.querySelector(
                        `[id='${this.getWidgetId}'] .itemControl_${i}`
                    ) as HTMLDivElement;
                    closeControl.classList.remove('active');
                    hiddenItem.classList.remove('active');
                    hiddenItem.style.height = '0px';
                }

                return (this.lastOpenItem = -1);
            } else {
                for (let i = 0; i < accordionList.length; i++) {
                    const hiddenItem = document.querySelector(
                        `[id='${this.getWidgetId}'] .itemContent_${i}`
                    ) as HTMLDivElement;
                    const closeControl = document.querySelector(
                        `[id='${this.getWidgetId}'] .itemControl_${i}`
                    ) as HTMLDivElement;
                    closeControl.classList.remove('active');
                    hiddenItem.classList.remove('active');
                    hiddenItem.style.height = '0px';
                }
                openControl.classList.add('active');
                visibleItem.classList.add('active');
                visibleItem.style.height = visibleItemHeight;

                setTimeout(() => {
                    visibleItem.style.height = 'auto';
                }, 350);

                return (this.lastOpenItem = index);
            }
        } else {
            const currentItem = document.querySelector(
                `[id='${this.getWidgetId}'] .itemContent_${index}`
            ) as HTMLDivElement;
            const currentControl = document.querySelector(
                `[id='${this.getWidgetId}'] .itemControl_${index}`
            ) as HTMLDivElement;

            if (currentItem.classList.contains('active')) {
                currentControl.classList.remove('active');
                currentItem.classList.remove('active');
                currentItem.style.height = '0px';
            } else {
                currentControl.classList.add('active');
                currentItem.classList.add('active');
                currentItem.style.height = visibleItemHeight;

                setTimeout(() => {
                    currentItem.style.height = 'auto';
                }, 350);
            }
        }
    }
}
